import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { SettingToggle } from "@/components/Interface/SettingToggle/SettingToggle";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { useBlockedVoiceSettingsReason } from "@/components/Portal/DeviceManagement/VoiceSettings/useBlockedVoiceSettingsReason";
import { useAlert } from "@/hooks/useAlert";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import {
  getGetVoiceSettingsQueryKey,
  useGetSim,
  useGetVoiceSettings,
  useUpdateVoiceSettings,
} from "@/services/api";
import { VoiceSettings as VoiceSettingsType } from "@/services/model";
import { createVoiceSettingsErrorHandler } from "@/utils/voicesettingserrorhandler";

/**
 * The voice settings tab of the device detail view: which kinds of calls and
 * value-added services this SIM may use. Read-only until the customer opens
 * the card for editing.
 */
export const VoiceSettings: FC = () => {
  const [draft, setDraft] = useState<VoiceSettingsType>();
  const [simId] = useSimIdFromQueryParam();

  const [mode, setMode] = useQueryParam("mode");
  const { t } = useTranslation();
  const { t: tSettings } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.voiceSettings.settings",
  });
  const queryClient = useQueryClient();
  const showAlert = useAlert();

  const parsedSimId = parseInt(simId);
  const hasSimId = !!simId && !Number.isNaN(parsedSimId);
  const voiceSettingsSimId = Number.isNaN(parsedSimId) ? -1 : parsedSimId;

  const { data: simDetail } = useGetSim(
    { simId: voiceSettingsSimId },
    { query: { enabled: hasSimId } },
  );

  const { data: voiceSettings } = useGetVoiceSettings(
    { simId: voiceSettingsSimId },
    { query: { enabled: hasSimId } },
  );

  const closeEditMode = () => {
    setDraft(undefined);
    setMode("", { replace: true });
  };

  const { mutate: updateVoiceSettings } = useUpdateVoiceSettings({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(
          getGetVoiceSettingsQueryKey({ simId: voiceSettingsSimId }),
          data,
        );

        closeEditMode();
      },
      onError: (error) => {
        createVoiceSettingsErrorHandler(t, showAlert)(error);
      },
    },
  });

  const isServicePending = !!simDetail?.servicePending;
  // While the query is in flight there is nothing to switch yet, so the rows
  // stay locked — but "not answered yet" is not a reason the customer can act
  // on, and it must not decide anything beyond their appearance.
  const hasVoiceSettings = voiceSettings !== undefined;
  const blocked = !voiceSettings?.canChangeSettings || isServicePending;
  const isBlockedForGood = hasVoiceSettings && blocked;
  // `?mode=edit` can be typed into the URL, so blocked settings never open for
  // editing — otherwise the card offers a save button that cannot do anything
  // and hides the hint explaining why.
  const isChangingVoiceSettings = mode === "edit" && !blocked;

  // The tab strip greys out its other tabs while `mode=edit` stands in the URL.
  // A blocked card never enters edit mode and so never renders the cancel
  // button that would clear it — drop the parameter instead of stranding the
  // customer on a view where nothing else is reachable.
  useEffect(() => {
    if (mode === "edit" && isBlockedForGood) {
      setMode("", { replace: true });
    }
  }, [mode, isBlockedForGood, setMode]);

  // A different SIM must not inherit the previous one's unsaved edits: the
  // component survives a change of `?simId=`, and `save()` would PUT them.
  useEffect(() => {
    setDraft(undefined);
  }, [simId]);

  // Edits live in a draft that starts out as whatever the API returned, so
  // cancelling discards them and a cached query (whose onSuccess never fires
  // again) still yields editable switches.
  const currentVoiceSettings = isChangingVoiceSettings
    ? (draft ?? voiceSettings)
    : voiceSettings;

  const toggle = (
    setting:
      | "roamingAndInternationalCalls"
      | "valueAddedServiceNumber"
      | "valueAddedServiceMessages"
      | "adultEntertainment"
      | "voiceMail",
  ) => {
    setDraft((previous) => {
      const base = previous ?? voiceSettings ?? {};
      const next = {
        ...base,
        //Convert booleans to integers as the backend schema is configured to accept integer($int64)
        [setting]: base[setting] ? 0 : 1,
      };

      // Adult entertainment rides on value-added messages, so switching those
      // off switches it off too.
      if (setting === "valueAddedServiceMessages" && base[setting]) {
        next.adultEntertainment = 0;
      }

      return next;
    });
  };
  const blockedReason = useBlockedVoiceSettingsReason({
    blocked: isBlockedForGood,
    isServicePending,
    reasons: voiceSettings?.reason,
  });

  const settings = [
    {
      title: tSettings("roaming.title"),
      subtitle: (
        <Trans
          i18nKey="cockpit.managementTile.voiceSettings.settings.roaming.subtitle"
          components={[
            <Link
              key="termsAndConditionsLink"
              target="_blank"
              href={t("Common.links.rates")}
              size="sm"
              rel="noreferrer"
            />,
          ]}
        />
      ),
      state: !!currentVoiceSettings?.roamingAndInternationalCalls,
      onToggle: () => toggle("roamingAndInternationalCalls"),
      blocked,
    },
    {
      title: tSettings("serviceNumbers.title"),
      subtitle: tSettings("serviceNumbers.subtitle"),
      state: !!currentVoiceSettings?.valueAddedServiceNumber,
      onToggle: () => toggle("valueAddedServiceNumber"),
      blocked,
    },
    {
      title: tSettings("serviceSMS.title"),
      subtitle: tSettings("serviceSMS.subtitle"),
      state: !!currentVoiceSettings?.valueAddedServiceMessages,
      onToggle: () => toggle("valueAddedServiceMessages"),
      blocked,
    },
    {
      title: tSettings("adult.title"),
      subtitle: tSettings("adult.subtitle"),
      state: !!currentVoiceSettings?.adultEntertainment,
      onToggle: () => toggle("adultEntertainment"),
      blocked: blocked || !currentVoiceSettings?.valueAddedServiceMessages,
    },
    {
      title: tSettings("voiceBox.title"),
      subtitle: tSettings("voiceBox.subtitle"),
      state: !!currentVoiceSettings?.voiceMail,
      onToggle: () => toggle("voiceMail"),
      blocked,
    },
  ];

  const save = () => {
    if (!simId || !currentVoiceSettings || blocked) {
      return;
    }
    updateVoiceSettings({ data: currentVoiceSettings });
  };

  return (
    <TwoColumnLayout
      rows={[
        {
          left: (
            <CardModule
              dataTestid="voice-settings"
              stickyHeader={isChangingVoiceSettings}
              title={t("portal:cockpit.managementTile.tabs.voice")}
              actions={
                isChangingVoiceSettings ? (
                  <>
                    <Button
                      dataTestid="voice-settings-cancel"
                      size="sm"
                      variant="secondary"
                      onClick={closeEditMode}
                    >
                      {t("Common.label.cancel")}
                    </Button>
                    <Button
                      dataTestid="voice-settings-save"
                      size="sm"
                      variant="primary"
                      onClick={save}
                    >
                      {t("Common.label.save")}
                    </Button>
                  </>
                ) : (
                  !blocked && (
                    <Button
                      dataTestid="voice-settings-change"
                      size="sm"
                      variant="primary"
                      onClick={() => setMode("edit", { replace: true })}
                    >
                      {t("Common.label.change")}
                    </Button>
                  )
                )
              }
              bodyGap={2}
            >
              {settings.map(({ title, subtitle, state, blocked, onToggle }) => (
                <SettingToggle
                  key={title}
                  checked={state}
                  title={title}
                  subtitle={subtitle}
                  blocked={blocked}
                  readOnly={!isChangingVoiceSettings}
                  onToggle={onToggle}
                />
              ))}
            </CardModule>
          ),
          // The hint explains why the settings are locked; while editing, the
          // customer is already past that point.
          right: !isChangingVoiceSettings && blockedReason && (
            <Notification
              dataTestid="voice-settings-hint"
              title={t("Content.goodToKnow")}
              description={blockedReason}
            />
          ),
        },
      ]}
    />
  );
};
